import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import {
  canUseProductSearchIndex,
  getProductSearchIndexMatches,
  renderProductSearchGridResults,
} from './product-search-index.js';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#loadProductSearchIndexResults();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };

  async #loadProductSearchIndexResults() {
    const searchTerm = this.refs.searchPageInput.value.trim();
    if (!canUseProductSearchIndex(searchTerm)) return;

    const noResults = this.refs.searchPageInput.form?.querySelector('.search-results__no-results');
    const resultsGrid = document.querySelector('#ResultsList .product-grid');
    if (!(resultsGrid instanceof HTMLElement)) return;

    const matches = await getProductSearchIndexMatches(searchTerm, this.#controller.signal);
    if (!matches?.length || this.#controller.signal.aborted) return;

    const existingProductIds = noResults
      ? new Set()
      : new Set(
          Array.from(resultsGrid.querySelectorAll('[data-product-id]')).map((element) =>
            element.getAttribute('data-product-id')
          )
        );
    const newMatches = matches.filter((match) => !existingProductIds.has(String(match.item.id)));
    const renderedResults = renderProductSearchGridResults(newMatches);
    if (!renderedResults) return;

    if (noResults) {
      noResults.remove();
      resultsGrid.replaceChildren(renderedResults);
      document.querySelector('.main-collection-grid__title')?.remove();
      return;
    }

    resultsGrid.appendChild(renderedResults);
  }
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
